/**
 * ACRES Admin — Task Template Manager (CRUD)
 */
import React, { useEffect, useState } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

const PLATFORMS = ['', 'facebook','instagram','tiktok','youtube','twitter','linkedin','telegram','reddit','threads','snapchat','whatsapp'];
const LEVELS = [1, 2, 3, 4, 5];

interface Template {
  id: string; title: string; description?: string; level: number; points: number;
  platform?: string | null; estimated_minutes?: number | null;
  instructions?: string | null; proof_requirements?: string | null;
  is_active: boolean; max_submissions_per_user?: number | null;
  cooldown_hours: number; requires_two_step: boolean;
  start_at?: string | null; end_at?: string | null; sort_order: number;
}

const emptyForm: Partial<Template> = { title: '', level: 1, points: 50, platform: '', cooldown_hours: 0, is_active: true };

export const AcresTaskTemplates: React.FC<{ currentUser?: any }> = () => {
  const [items, setItems] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Partial<Template> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await acresAdmin.listTemplates({ includeInactive: true }); setItems(r.templates || []); }
    catch (e: any) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        const { id, ...patch } = editing as any;
        await acresAdmin.updateTemplate(id, patch);
      } else {
        await acresAdmin.createTemplate(editing as any);
      }
      setEditing(null); await load();
    } catch (e: any) { alert(e?.message || 'Save failed'); }
  };

  const toggleActive = async (t: Template) => {
    try { await acresAdmin.setTemplateActive(t.id, !t.is_active); await load(); }
    catch (e: any) { alert(e?.message || 'Toggle failed'); }
  };

  const remove = async (t: Template) => {
    if (!confirm(`Delete template "${t.title}"? This is only allowed if no submissions exist.`)) return;
    try { await acresAdmin.deleteTemplate(t.id); await load(); }
    catch (e: any) { alert(e?.message || 'Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Task Templates ({items.length})</div>
        <button onClick={() => setEditing({ ...emptyForm })} style={btnPrimary}>+ New Template</button>
      </div>
      {error && <div style={errBox}>{error}</div>}
      {loading && <div style={{ color: '#9CA3AF' }}>Loading…</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={trHead}>
              <th style={th}>Title</th><th style={th}>Level</th><th style={th}>Points</th>
              <th style={th}>Platform</th><th style={th}>2-Step</th><th style={th}>Active</th><th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(t => (
              <tr key={t.id} style={trBody}>
                <td style={td}><div style={{ fontWeight: 600 }}>{t.title}</div><div style={subText}>{t.description}</div></td>
                <td style={td}>L{t.level}</td>
                <td style={td}>{t.points}</td>
                <td style={td}>{t.platform || '—'}</td>
                <td style={td}>{t.requires_two_step ? '✅' : '—'}</td>
                <td style={td}>{t.is_active ? '🟢' : '⚪'}</td>
                <td style={td}>
                  <button onClick={() => setEditing(t)} style={btnSmall}>Edit</button>
                  <button onClick={() => toggleActive(t)} style={btnSmall}>{t.is_active ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => remove(t)} style={{ ...btnSmall, color: '#F87171' }}>Delete</button>
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>No templates yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{editing.id ? 'Edit Template' : 'New Template'}</div>
            <div style={grid}>
              <Field label="Title*"><input style={input} value={editing.title || ''} onChange={e => setEditing({ ...editing, title: e.target.value })} /></Field>
              <Field label="Level*"><select style={input} value={editing.level || 1} onChange={e => setEditing({ ...editing, level: Number(e.target.value) })}>{LEVELS.map(l => <option key={l} value={l}>L{l}</option>)}</select></Field>
              <Field label="Points*"><input type="number" style={input} value={editing.points ?? 0} onChange={e => setEditing({ ...editing, points: Number(e.target.value) })} /></Field>
              <Field label="Platform"><select style={input} value={editing.platform || ''} onChange={e => setEditing({ ...editing, platform: e.target.value || null })}>{PLATFORMS.map(p => <option key={p} value={p}>{p || '— any —'}</option>)}</select></Field>
              <Field label="Estimated minutes"><input type="number" style={input} value={editing.estimated_minutes || ''} onChange={e => setEditing({ ...editing, estimated_minutes: Number(e.target.value) || null })} /></Field>
              <Field label="Cooldown hours"><input type="number" style={input} value={editing.cooldown_hours || 0} onChange={e => setEditing({ ...editing, cooldown_hours: Number(e.target.value) })} /></Field>
              <Field label="Max submissions/user"><input type="number" style={input} value={editing.max_submissions_per_user || ''} onChange={e => setEditing({ ...editing, max_submissions_per_user: Number(e.target.value) || null })} /></Field>
              <Field label="Sort order"><input type="number" style={input} value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></Field>
              <Field label="Start at (UTC)"><input type="datetime-local" style={input} value={editing.start_at?.slice(0,16) || ''} onChange={e => setEditing({ ...editing, start_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></Field>
              <Field label="End at (UTC)"><input type="datetime-local" style={input} value={editing.end_at?.slice(0,16) || ''} onChange={e => setEditing({ ...editing, end_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></Field>
            </div>
            <Field label="Description"><textarea style={{ ...input, minHeight: 60 }} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} /></Field>
            <Field label="Instructions"><textarea style={{ ...input, minHeight: 80 }} value={editing.instructions || ''} onChange={e => setEditing({ ...editing, instructions: e.target.value })} /></Field>
            <Field label="Proof requirements"><textarea style={{ ...input, minHeight: 60 }} value={editing.proof_requirements || ''} onChange={e => setEditing({ ...editing, proof_requirements: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!editing.requires_two_step} onChange={e => setEditing({ ...editing, requires_two_step: e.target.checked })} /> Requires 2 moderators</label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setEditing(null)} style={btnGhost}>Cancel</button>
              <button onClick={save} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}><div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>{label}</div>{children}</div>
);
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
const subText: React.CSSProperties = { fontSize: 11, color: '#9CA3AF', marginTop: 2 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13 };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 };
const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard: React.CSSProperties = { background: '#121212', border: '1px solid #2A2A2A', borderRadius: 12, padding: 20, width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto' };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', marginRight: 6, background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
